/**
 * @file useBrickValidation.js
 * @description BRICK class validation helpers composable
 *
 * Extracted from UnifiedBrickSchemaManager.vue
 * Handles:
 * - BRICK class formatting and display
 * - BRICK class validation
 * - Point type inference from BRICK class
 * - Equipment type detection from BRICK class
 */

// No Vue imports needed - this composable uses pure functions

/**
 * Format BRICK class for display (remove prefix).
 * Standalone export for use without the full composable.
 * @param {string} brickClass - Full BRICK class URI
 * @returns {string} Display-friendly class name
 */
export function formatBrickClass(brickClass) {
  if (!brickClass) {return '';}

  return brickClass
    .replace(/^brick:/i, '')
    .replace(/^https?:\/\/brickschema\.org\/schema\/[^#]*#/i, '')
    .replace(/_/g, ' ');
}

/**
 * Composable for BRICK Schema validation helpers
 * @returns {Object} BRICK validation methods
 */
export function useBrickValidation() {
  // =============================================
  // BRICK CLASS FORMATTING
  // =============================================

  // Use the standalone formatBrickClass function

  /**
   * Get full BRICK class with prefix
   * @param {string} className - Class name without prefix
   * @returns {string} Full BRICK class URI
   */
  const toBrickClass = (className) => {
    if (!className) {return '';}

    // Already has prefix
    if (className.startsWith('brick:') || className.startsWith('http')) {
      return className;
    }

    return `brick:${className.replace(/ /g, '_')}`;
  };

  /**
   * Extract class name from full BRICK URI
   * @param {string} brickUri - Full BRICK class URI
   * @returns {string} Class name only
   */
  const extractClassName = (brickUri) => {
    if (!brickUri) {return '';}

    // Handle brick: prefix
    if (brickUri.startsWith('brick:')) {
      return brickUri.replace('brick:', '');
    }

    // Handle full URI
    const hashIndex = brickUri.indexOf('#');
    if (hashIndex > -1) {
      return brickUri.substring(hashIndex + 1);
    }

    return brickUri;
  };

  // =============================================
  // BRICK CLASS VALIDATION
  // =============================================

  /**
   * Check if class is a valid equipment class
   * @param {string} brickClass - BRICK class to check
   * @returns {boolean}
   */
  const isEquipmentClass = (brickClass) => {
    if (!brickClass) {return false;}

    const className = extractClassName(brickClass).toLowerCase();

    // Common equipment class patterns
    const equipmentPatterns = [
      'air_handling_unit',
      'ahu',
      'variable_air_volume',
      'vav',
      'chiller',
      'boiler',
      'pump',
      'fan',
      'fan_coil_unit',
      'fcu',
      'heat_exchanger',
      'cooling_tower',
      'condenser',
      'compressor',
      'valve',
      'damper',
      'filter',
      'humidifier',
      'dehumidifier',
      'thermostat',
      'sensor',
      'meter',
      'lighting',
      'elevator',
      'hvac_equipment',
      'electrical_equipment',
      'plumbing_equipment',
      'fire_alarm',
      'security_equipment',
    ];

    return equipmentPatterns.some((pattern) =>
      className.includes(pattern.toLowerCase()),
    );
  };

  /**
   * Check if class is a valid device class
   * @param {string} brickClass - BRICK class to check
   * @returns {boolean}
   */
  const isDeviceClass = (brickClass) => {
    if (!brickClass) {return false;}

    const className = extractClassName(brickClass).toLowerCase();

    // Common device class patterns
    const devicePatterns = [
      'sensor',
      'meter',
      'actuator',
      'controller',
      'thermostat',
      'switch',
      'relay',
      'transducer',
      'transmitter',
      'detector',
      'monitor',
      'gauge',
    ];

    return devicePatterns.some((pattern) =>
      className.includes(pattern.toLowerCase()),
    );
  };

  /**
   * Check if class is a valid point class
   * @param {string} brickClass - BRICK class to check
   * @returns {boolean}
   */
  const isPointClass = (brickClass) => {
    if (!brickClass) {return false;}

    const className = extractClassName(brickClass).toLowerCase();

    // Common point class patterns
    const pointPatterns = [
      'point',
      'setpoint',
      'sensor',
      'status',
      'command',
      'alarm',
      'parameter',
      'temperature',
      'humidity',
      'pressure',
      'flow',
      'position',
      'speed',
      'power',
      'energy',
      'level',
    ];

    return pointPatterns.some((pattern) =>
      className.includes(pattern.toLowerCase()),
    );
  };

  /**
   * Check if class is a valid space class
   * @param {string} brickClass - BRICK class to check
   * @returns {boolean}
   */
  const isSpaceClass = (brickClass) => {
    if (!brickClass) {return false;}

    const className = extractClassName(brickClass).toLowerCase();

    // Common space class patterns
    const spacePatterns = [
      'space',
      'room',
      'zone',
      'area',
      'floor',
      'level',
      'building',
      'wing',
      'lobby',
      'corridor',
      'office',
      'restroom',
      'kitchen',
      'conference',
      'storage',
      'mechanical',
      'electrical',
      'data_center',
      'server_room',
      'parking',
    ];

    return spacePatterns.some((pattern) =>
      className.includes(pattern.toLowerCase()),
    );
  };

  /**
   * Check if class is an AHU-related class
   * @param {string} brickClass - BRICK class to check
   * @returns {boolean}
   */
  const isAhuClass = (brickClass) => {
    if (!brickClass) {return false;}

    const className = extractClassName(brickClass).toLowerCase();

    return className.includes('air_handling_unit') ||
           className.includes('ahu') ||
           className === 'ahu_zone_section';
  };

  /**
   * Check if class is a controller class
   * @param {string} brickClass - BRICK class to check
   * @returns {boolean}
   */
  const isControllerClass = (brickClass) => {
    if (!brickClass) {return false;}

    const className = extractClassName(brickClass).toLowerCase();

    return className.includes('controller') ||
           className.includes('ddc') ||
           className.includes('plc') ||
           className.includes('bms');
  };

  /**
   * Check if class is a switchboard/electrical class
   * @param {string} brickClass - BRICK class to check
   * @returns {boolean}
   */
  const isElectricalClass = (brickClass) => {
    if (!brickClass) {return false;}

    const className = extractClassName(brickClass).toLowerCase();

    return className.includes('switchboard') ||
           className.includes('msb') ||
           className.includes('mssb') ||
           className.includes('panel') ||
           className.includes('circuit') ||
           className.includes('breaker') ||
           className.includes('transformer') ||
           className.includes('ups') ||
           className.includes('generator');
  };

  // =============================================
  // POINT TYPE INFERENCE
  // =============================================

  /**
   * Infer point type from BRICK class
   * @param {string} brickClass - BRICK point class
   * @returns {string} Point type (Analog, Binary, MultiState, Virtual)
   */
  const inferPointType = (brickClass) => {
    if (!brickClass) {return 'Virtual';}

    const className = extractClassName(brickClass).toLowerCase();

    // Binary points
    if (className.includes('status') ||
        className.includes('command') ||
        className.includes('alarm') ||
        className.includes('switch') ||
        className.includes('enable') ||
        className.includes('disable') ||
        className.includes('on_off') ||
        className.includes('binary')) {
      return 'Binary';
    }

    // MultiState points
    if (className.includes('mode') ||
        className.includes('state') ||
        className.includes('position') ||
        className.includes('multi') ||
        className.includes('stage')) {
      return 'MultiState';
    }

    // Analog points (most sensor/setpoint types)
    if (className.includes('temperature') ||
        className.includes('humidity') ||
        className.includes('pressure') ||
        className.includes('flow') ||
        className.includes('speed') ||
        className.includes('power') ||
        className.includes('energy') ||
        className.includes('level') ||
        className.includes('percent') ||
        className.includes('setpoint') ||
        className.includes('sensor') ||
        className.includes('analog')) {
      return 'Analog';
    }

    // Default to Virtual for unknown types
    return 'Virtual';
  };

  /**
   * Infer quantity kind from BRICK class
   * @param {string} brickClass - BRICK class
   * @returns {string|null} Quantity kind or null
   */
  const inferQuantityKind = (brickClass) => {
    if (!brickClass) {return null;}

    const className = extractClassName(brickClass).toLowerCase();

    // Temperature
    if (className.includes('temperature') || className.includes('temp')) {
      return 'Temperature';
    }

    // Humidity
    if (className.includes('humidity') || className.includes('rh')) {
      return 'RelativeHumidity';
    }

    // Pressure
    if (className.includes('pressure')) {
      if (className.includes('static')) {return 'StaticPressure';}
      if (className.includes('differential')) {return 'DifferentialPressure';}
      return 'Pressure';
    }

    // Flow
    if (className.includes('flow')) {
      if (className.includes('air')) {return 'AirFlow';}
      if (className.includes('water')) {return 'WaterFlow';}
      return 'Flow';
    }

    // Power/Energy
    if (className.includes('power')) {return 'Power';}
    if (className.includes('energy')) {return 'Energy';}

    // Speed
    if (className.includes('speed') || className.includes('frequency')) {
      return 'Speed';
    }

    // Position/Percentage
    if (className.includes('position') || className.includes('percent')) {
      return 'Percent';
    }

    // Level
    if (className.includes('level')) {return 'Level';}

    // CO2
    if (className.includes('co2') || className.includes('carbon_dioxide')) {
      return 'CO2Concentration';
    }

    return null;
  };

  /**
   * Infer unit from BRICK class
   * @param {string} brickClass - BRICK class
   * @returns {string|null} Unit abbreviation or null
   */
  const inferUnit = (brickClass) => {
    if (!brickClass) {return null;}

    const className = extractClassName(brickClass).toLowerCase();

    // Temperature (Celsius as default, could be configurable)
    if (className.includes('temperature') || className.includes('temp')) {
      return 'degC';
    }

    // Humidity
    if (className.includes('humidity') || className.includes('rh')) {
      return '%RH';
    }

    // Pressure
    if (className.includes('pressure')) {
      return 'Pa';
    }

    // Flow
    if (className.includes('flow')) {
      if (className.includes('air')) {return 'L/s';}
      if (className.includes('water')) {return 'L/s';}
      return 'L/s';
    }

    // Power
    if (className.includes('power')) {return 'kW';}

    // Energy
    if (className.includes('energy')) {return 'kWh';}

    // Speed
    if (className.includes('speed')) {return 'Hz';}

    // Position/Percentage
    if (className.includes('position') || className.includes('percent')) {
      return '%';
    }

    // CO2
    if (className.includes('co2') || className.includes('carbon_dioxide')) {
      return 'ppm';
    }

    return null;
  };

  // =============================================
  // EQUIPMENT TYPE DETECTION
  // =============================================

  /**
   * Get equipment icon based on BRICK class
   * @param {string} brickClass - BRICK class
   * @returns {string} Material icon name
   */
  const getEquipmentIcon = (brickClass) => {
    if (!brickClass) {return 'precision_manufacturing';}

    const className = extractClassName(brickClass).toLowerCase();

    // AHU/HVAC
    if (className.includes('air_handling') || className.includes('ahu')) {
      return 'air';
    }

    // VAV
    if (className.includes('variable_air') || className.includes('vav')) {
      return 'tune';
    }

    // Chiller
    if (className.includes('chiller')) {return 'ac_unit';}

    // Boiler
    if (className.includes('boiler')) {return 'whatshot';}

    // Pump
    if (className.includes('pump')) {return 'water_pump';}

    // Fan
    if (className.includes('fan')) {return 'mode_fan';}

    // Controller
    if (className.includes('controller') || className.includes('ddc')) {
      return 'memory';
    }

    // Electrical
    if (className.includes('switchboard') || className.includes('panel')) {
      return 'electrical_services';
    }

    // Sensor
    if (className.includes('sensor')) {return 'sensors';}

    // Meter
    if (className.includes('meter')) {return 'speed';}

    // Lighting
    if (className.includes('lighting') || className.includes('light')) {
      return 'lightbulb';
    }

    // Default
    return 'precision_manufacturing';
  };

  /**
   * Get equipment color based on BRICK class
   * @param {string} brickClass - BRICK class
   * @returns {string} Quasar color name
   */
  const getEquipmentColor = (brickClass) => {
    if (!brickClass) {return 'grey';}

    const className = extractClassName(brickClass).toLowerCase();

    // AHU/HVAC - blue
    if (className.includes('air_handling') || className.includes('ahu')) {
      return 'blue';
    }

    // VAV - light blue
    if (className.includes('variable_air') || className.includes('vav')) {
      return 'light-blue';
    }

    // Chiller - cyan
    if (className.includes('chiller')) {return 'cyan';}

    // Boiler - red
    if (className.includes('boiler')) {return 'red';}

    // Pump - teal
    if (className.includes('pump')) {return 'teal';}

    // Fan - indigo
    if (className.includes('fan')) {return 'indigo';}

    // Controller - purple
    if (className.includes('controller') || className.includes('ddc')) {
      return 'purple';
    }

    // Electrical - amber
    if (className.includes('switchboard') || className.includes('panel')) {
      return 'amber';
    }

    // Sensor - green
    if (className.includes('sensor')) {return 'green';}

    // Default
    return 'grey-7';
  };

  return {
    // Formatting
    formatBrickClass,
    toBrickClass,
    extractClassName,

    // Validation
    isEquipmentClass,
    isDeviceClass,
    isPointClass,
    isSpaceClass,
    isAhuClass,
    isControllerClass,
    isElectricalClass,

    // Point type inference
    inferPointType,
    inferQuantityKind,
    inferUnit,

    // Equipment type detection
    getEquipmentIcon,
    getEquipmentColor,
  };
}
